"use client";

import { useState, useEffect, useMemo } from "react";
import clsx from "clsx";

const DEFAULT_STORAGE_KEY = "toc:visible";

export default function Toc({
    headings = [],
    label = "On this page",
    className = "",
    sticky = true,
    stickyOffset = 96,
    desktop = "right",
    storageKey = DEFAULT_STORAGE_KEY,
    rememberVisibility = true,
}) {
    const hasHeadings = headings?.length > 0;
    const [activeId, setActiveId] = useState(null);

    // ── Visibility persistence (desktop Hide/Show) ─────────────────────────────
    const [visible, setVisible] = useState(true);

    // Load saved state once
    useEffect(() => {
        if (!rememberVisibility) return;
        try {
            const raw = sessionStorage.getItem(storageKey);
            if (raw != null) setVisible(raw === "1");
        } catch {}
    }, [rememberVisibility, storageKey]);

    // Persist on change
    useEffect(() => {
        if (!rememberVisibility) return;
        try {
            sessionStorage.setItem(storageKey, visible ? "1" : "0");
        } catch {}
    }, [visible, rememberVisibility, storageKey]);

    // ── Scroll-spy (desktop & mobile) ──────────────────────────────────────────
    useEffect(() => {
        if (!hasHeadings) return;

        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach(
                    (entry) =>
                        entry.isIntersecting && setActiveId(entry.target.id)
                );
            },
            { rootMargin: "0% 0% -70% 0%", threshold: 0 }
        );

        headings.forEach((h) => {
            const el = document.getElementById(h.id);
            if (el) obs.observe(el);
        });

        return () => obs.disconnect();
    }, [hasHeadings, headings]);

    const stickyStyle = useMemo(
        () => (sticky ? { position: "sticky", top: `${stickyOffset}px` } : {}),
        [sticky, stickyOffset]
    );

    if (!hasHeadings) return null;

    // Mobile: manage <details> independently but initialize from `visible`
    const [mobileOpen, setMobileOpen] = useState(null);
    useEffect(() => {
        setMobileOpen(visible);
    }, [visible]);

    return (
        <section className={className} data-toc-desktop={desktop}>
            {/* Mobile: collapsible */}
            <details
                className="lg:hidden mb-6 rounded-md border bg-white/70 dark:bg-zinc-900/50"
                open={mobileOpen ?? visible}
                onToggle={(e) => setMobileOpen(e.currentTarget.open)}
            >
                <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">
                    {label}
                </summary>
                <div className="px-4 pb-3">
                    <ol className="space-y-1">
                        {headings.map((h) => (
                            <li
                                key={h.id}
                                className={
                                    h.depth === 2
                                        ? "pl-0"
                                        : h.depth === 3
                                        ? "pl-3"
                                        : "pl-6"
                                }
                            >
                                <a
                                    href={`#${h.id}`}
                                    className={clsx(
                                        "block text-sm leading-6 hover:underline",
                                        activeId === h.id
                                            ? "text-blue-600 font-medium"
                                            : ""
                                    )}
                                >
                                    {h.value}
                                </a>
                            </li>
                        ))}
                    </ol>
                </div>
            </details>

            {/* Desktop: sticky + hide/show + scroll-spy */}
            <nav
                aria-label={label}
                className="hidden lg:block"
                style={stickyStyle}
            >
                <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {label}
                    </span>
                    <button
                        type="button"
                        onClick={() => setVisible((v) => !v)}
                        className="text-xs text-blue-600 hover:underline focus:outline-none"
                    >
                        {visible ? "Hide" : "Show"}
                    </button>
                </div>

                {visible && (
                    <ol className="space-y-1 max-h-[calc(100vh-8rem)] overflow-auto pr-2">
                        {headings.map((h) => (
                            <li
                                key={h.id}
                                className={
                                    h.depth === 2
                                        ? "pl-0"
                                        : h.depth === 3
                                        ? "pl-3"
                                        : "pl-6"
                                }
                            >
                                <a
                                    href={`#${h.id}`}
                                    title={h.value}
                                    className={clsx(
                                        "block text-sm leading-6 truncate hover:underline",
                                        activeId === h.id
                                            ? "text-blue-600 font-medium"
                                            : "text-gray-700"
                                    )}
                                >
                                    {h.value}
                                </a>
                            </li>
                        ))}
                    </ol>
                )}
            </nav>
        </section>
    );
}
